/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { withTheme } from '@emotion/react';
import { Button, Grid, Toolbar, Typography, Menu, Fade, MenuItem } from '@mui/material';
import UseTabContainer from 'modules/dashboard/hooks/use-tab-container';
import { ExpandMore, Menu as MenuIcon } from '@mui/icons-material';
import { selectAuthUser } from 'modules/common/auth/selectors';
import { ADMIN_ROLE, PREFERRED_ADMIN_ROLE, SUPER_ADMIN_ROLE } from 'modules/common/constants/roles';
import ROUTES from 'modules/common/constants/route';
import { ENVIRONMENT } from 'config';
import { isEmpty } from 'modules/common/helpers/object';
import { faroSendHotelBasedUserActivity } from 'modules/common/utils/faro';
import NavbarUserDropdown from './navbar-user-dropdown';
import NavbarHotelDropdown from './navbar-hotel-dropdown';
import { AppBar, IconButton, LargeAvatar, MenuItemDropdownItem, SmallAvatar } from './style';
import NavbarCurrencyDropdown from './navbar-currency-breakdown';
import {
  selectAppTheme,
  selectEnableHotelGroups,
  selectHotelName,
} from '../../../dashboard/selectors';
import THEMES from '../../constants/themes';
import { dashboardActions } from '../../../dashboard/slice';
import variants from '../../theme/variants';
import AnnouncementBlock from './announcement-bar';
/**
 * Navigation bar common component that uses
 * across modules for navigate among pages
 */
const Navbar = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  // get current path location
  const location = useLocation();
  //
  const currentUser = useSelector(selectAuthUser);
  const selectedHotelName = useSelector(selectHotelName);
  const enableHotelGroups = useSelector(selectEnableHotelGroups);
  const themeName = useSelector(selectAppTheme);
  const selectedVariant = variants.find((variant) => variant.name === themeName);
  //
  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorMenu, setAnchorMenu] = useState(null);
  const [appLogo, setAppLogo] = useState('');
  //
  const closeMenu = () => {
    setAnchorMenu(null);
  };
  //
  const toggleMenu = (event) => {
    setAnchorMenu(event.currentTarget);
  };
  // set initial selected path state
  const [selected, setSelected] = useState({
    home: location.pathname === ROUTES.DASHBOARD,
    rateCalendar: location.pathname === ROUTES.RATE_CALENDAR,
    forecast: location.pathname === ROUTES.FORECAST_UPLOAD,
    configure:
      location.pathname === ROUTES.CONFIGURE_HOTELS ||
      location.pathname === ROUTES.EVENT_CATEGORIES ||
      location.pathname === ROUTES.USERS,
  });
  // set selected menu item based on changed location
  useEffect(() => {
    setSelected({
      home: location.pathname === ROUTES.DASHBOARD,
      rateCalendar: location.pathname === ROUTES.RATE_CALENDAR,
      forecast: location.pathname === ROUTES.FORECAST_UPLOAD,
      configure:
        location.pathname === ROUTES.CONFIGURE_HOTELS ||
        location.pathname === ROUTES.EVENT_CATEGORIES ||
        location.pathname === ROUTES.USERS,
    });
  }, [location]);
  //
  UseTabContainer();
  //
  useEffect(() => {
    if (!selectedHotelName) return;
    //
    let selectedTheme;
    if (!enableHotelGroups) {
      selectedTheme = variants?.find((theme) => theme?.name === selectedHotelName?.theme);
    } else {
      const setTheme = new Set(selectedHotelName?.hotels?.map((hotel) => hotel.theme));
      selectedTheme =
        setTheme.size === 1 ? variants?.find((theme) => theme?.name === [...setTheme][0]) : null;
    }
    //
    selectedTheme = selectedTheme || variants?.find((theme) => theme?.name === THEMES.BLUE_VARIANT);
    if (localStorage.getItem('freshLogin')) {
      faroSendHotelBasedUserActivity(selectedHotelName);
      localStorage.removeItem('freshLogin');
    }
    if (selectedTheme) {
      dispatch(dashboardActions.setAppTheme(selectedTheme.name));
      dispatch(dashboardActions.setAppName(selectedTheme.appName));
      setAppLogo(selectedTheme.appLogo);
    }
  }, [selectedHotelName, enableHotelGroups]);
  //
  return (
    <AppBar
      className="app-navbar"
      position="sticky"
      elevation={0}
      sx={{ height: 75, maxHeight: 75, padding: 0, margin: 0 }}
    >
      <Toolbar>
        <Grid container alignItems="center" direction="row" wrap="wrap">
          <Grid
            item
            xs={1}
            cc={1}
            sx={{
              flexBasis: { md: 100, xl: 150 },
              maxWidth: { md: 100, xl: 150 },
              flexShrink: 0,
            }}
            container
            direction="row"
            justifyContent="center"
            alignItems="center"
            wrap="nowrap"
          >
            {appLogo && (
              <>
                <Grid
                  item
                  xs={12}
                  mt={-0.5}
                  sx={{
                    display: { xs: 'none', sm: 'none', md: 'none', lg: 'none', xl: 'block' },
                  }}
                >
                  <LargeAvatar
                    variant="square"
                    src={appLogo}
                    sx={{ '& .MuiAvatar-img': { objectFit: 'contain !important' } }}
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  sx={{
                    display: { xs: 'block', sm: 'block', md: 'block', lg: 'block', xl: 'none' },
                    mt: -4,
                  }}
                >
                  <SmallAvatar
                    variant="square"
                    src={appLogo}
                    sx={{ '& .MuiAvatar-img': { objectFit: 'contain !important' } }}
                  />
                </Grid>
              </>
            )}
          </Grid>
          <Grid
            item
            xs={8}
            cc={8}
            sx={{
              display: { md: 'grid' },
              gridTemplateColumns: { md: 'minmax(0, 1fr) auto minmax(min-content, 1fr)' },
              flex: { md: '1 1 0' },
              '& > :last-child': { gridColumn: { md: '1 / -1' } },
              maxWidth: { md: 'none' },
              minWidth: 0,
            }}
            container
            direction="row"
            justifyContent="flex-start"
            alignItems="center"
            wrap="wrap"
          >
            <Grid
              item
              xs={5}
              cc={5}
              sx={{
                display: { md: 'flex' },
                minWidth: 0,
                flexBasis: { lg: 'auto' },
                width: { lg: 'auto' },
                maxWidth: { lg: 'none' },
                flexShrink: 0,
              }}
              container
              direction="row"
              justifyContent="flex-start"
              alignItems="center"
              wrap="nowrap"
            >
              <Grid
                item
                sx={{
                  display: {
                    xs: 'block',
                    cc: 'block',
                    md: 'none',
                  },
                }}
              >
                <IconButton
                  color="inherit"
                  aria-label="Menu Items"
                  size="large"
                  id="menu"
                  aria-owns={anchorMenu ? 'mobile-app-bar' : undefined}
                  aria-haspopup="true"
                  aria-controls={anchorMenu ? 'mobile-app-bar' : undefined}
                  aria-expanded={anchorMenu ? 'true' : undefined}
                  onClick={toggleMenu}
                >
                  <MenuIcon />
                </IconButton>
                <Menu
                  id="mobile-app-bar"
                  anchorEl={anchorMenu}
                  open={Boolean(anchorMenu)}
                  onClose={closeMenu}
                >
                  <MenuItem
                    onClick={() => {
                      navigate(ROUTES.DASHBOARD);
                      setAnchorMenu(null);
                    }}
                    onClose={closeMenu}
                  >
                    Home
                  </MenuItem>
                  {false && (
                    <MenuItem
                      onClick={() => {
                        navigate(ROUTES.RATE_CALENDAR);
                        setAnchorMenu(null);
                      }}
                      onClose={closeMenu}
                    >
                      Rate Calendar
                    </MenuItem>
                  )}
                  {ENVIRONMENT.ENABLE_BULK_UPLOAD.toLowerCase() === 'true' && (
                    <MenuItem
                      onClick={() => {
                        navigate(ROUTES.FORECAST_UPLOAD);
                        setAnchorMenu(null);
                      }}
                      onClose={closeMenu}
                    >
                      Bulk Monthly Input
                    </MenuItem>
                  )}
                  {!isEmpty(currentUser) &&
                    (currentUser?.roles[0]?.name === ADMIN_ROLE ||
                      currentUser?.roles[0]?.name === SUPER_ADMIN_ROLE) && (
                      <MenuItem
                        onClick={() => {
                          navigate(ROUTES.CONFIGURE_HOTELS);
                          setAnchorMenu(null);
                        }}
                        onClose={closeMenu}
                      >
                        Configure Hotels
                      </MenuItem>
                    )}
                  {!isEmpty(currentUser) &&
                    (currentUser?.roles[0]?.name === ADMIN_ROLE ||
                      currentUser?.roles[0]?.name === SUPER_ADMIN_ROLE) && (
                      <MenuItem
                        onClick={() => {
                          navigate(ROUTES.EVENT_CATEGORIES);
                          setAnchorMenu(null);
                        }}
                        onClose={closeMenu}
                      >
                        Configure Event Categories
                      </MenuItem>
                    )}
                  {!isEmpty(currentUser) &&
                    (currentUser?.roles[0]?.name === ADMIN_ROLE ||
                      currentUser?.roles[0]?.name === SUPER_ADMIN_ROLE ||
                      currentUser?.roles[0]?.name === PREFERRED_ADMIN_ROLE) && (
                      <MenuItem
                        onClick={() => {
                          navigate(ROUTES.USERS);
                          setAnchorMenu(null);
                        }}
                        onClose={closeMenu}
                      >
                        Configure Users
                      </MenuItem>
                    )}
                </Menu>
              </Grid>
              <Grid item xs={12} cc={12} md={12} lg={12}>
                <Typography
                  noWrap
                  sx={{
                    color: '#e5e5e5',
                    display: {
                      xs: 'none',
                      sm: 'none',
                      cc: 'none',
                      md: 'none',
                      lg: 'block',
                    },
                    fontSize: 18,
                    whiteSpace: 'nowrap',
                  }}
                >
                  Revenue Optimization Platform
                </Typography>
              </Grid>
            </Grid>
            <Grid
              item
              xs="auto"
              sx={{
                display: {
                  xs: 'none',
                  cc: 'none',
                  md: 'flex',
                },
                flexShrink: 0,
                '& .MuiButton-root': { whiteSpace: 'nowrap' },
              }}
              container
              justifyContent="center"
              alignItems="center"
              wrap="nowrap"
            >
              <Button
                onClick={() => {
                  setSelected({
                    home: true,
                    rateCalendar: false,
                    forecast: false,
                    configure: false,
                  });
                  navigate(ROUTES.DASHBOARD);
                }}
                variant="text"
                sx={{
                  color: selected.home ? '#ffffff' : '#9e9e9e',
                  fontSize: { md: 13, lg: 15 },
                }}
              >
                Home
              </Button>
              {/* Rate Calendar is hidden as per client's request */}
              {false && (
                <Button
                  onClick={() => {
                    setSelected({
                      home: false,
                      rateCalendar: true,
                      forecast: false,
                      configure: false,
                    });
                    navigate(ROUTES.RATE_CALENDAR);
                  }}
                  variant="text"
                  sx={{
                    color: selected.rateCalendar ? '#ffffff' : '#9e9e9e',
                    fontSize: { md: 13, lg: 15 },
                  }}
                >
                  Rate Calendar
                </Button>
              )}
              {ENVIRONMENT.ENABLE_BULK_UPLOAD.toLowerCase() === 'true' && (
                <>
                  {false && (
                    <Button
                      id="forecast"
                      variant="text"
                      endIcon={<ExpandMore />}
                      sx={{
                        color: selected.forecast ? '#ffffff' : '#9e9e9e',
                        fontSize: { md: 13, lg: 15 },
                      }}
                      onClick={(event) => {
                        setSelected({
                          home: false,
                          rateCalendar: false,
                          forecast: true,
                          configure: false,
                        });
                        setAnchorEl(event?.currentTarget);
                      }}
                      aria-controls={anchorEl?.id === 'forecast' ? 'fade-menu' : undefined}
                      aria-haspopup="true"
                      aria-expanded={anchorEl?.id === 'forecast' ? 'true' : undefined}
                    >
                      Forecast
                    </Button>
                  )}
                  <Menu
                    anchorEl={anchorEl}
                    MenuListProps={{
                      style: {
                        backgroundColor: selectedVariant?.header.background,
                      },
                    }}
                    open={anchorEl?.id === 'forecast'}
                    onClose={() => setAnchorEl(null)}
                    TransitionComponent={Fade}
                  >
                    <MenuItemDropdownItem
                      onClick={() => {
                        navigate(ROUTES.FORECAST_UPLOAD);
                        setAnchorEl(null);
                      }}
                    >
                      Bulk Monthly Input
                    </MenuItemDropdownItem>
                  </Menu>
                </>
              )}
              {!isEmpty(currentUser) &&
                (currentUser?.roles[0]?.name === ADMIN_ROLE ||
                  currentUser?.roles[0]?.name === SUPER_ADMIN_ROLE ||
                  currentUser?.roles[0]?.name === PREFERRED_ADMIN_ROLE) && (
                  <>
                    <Button
                      id="configuration"
                      variant="text"
                      endIcon={<ExpandMore />}
                      sx={{
                        color: selected.configure ? '#ffffff' : '#9e9e9e',
                        fontSize: { md: 13, lg: 15 },
                        mr: 2,
                      }}
                      onClick={(event) => {
                        setSelected({
                          home: false,
                          rateCalendar: false,
                          forecast: false,
                          configure: true,
                        });
                        setAnchorEl(event?.currentTarget);
                      }}
                      aria-controls={anchorEl?.id === 'configuration' ? 'fade-menu' : undefined}
                      aria-haspopup="true"
                      aria-expanded={anchorEl?.id === 'configuration' ? 'true' : undefined}
                    >
                      Configuration
                    </Button>
                    <Menu
                      anchorEl={anchorEl}
                      MenuListProps={{
                        style: {
                          backgroundColor: selectedVariant?.header.background,
                        },
                      }}
                      open={anchorEl?.id === 'configuration'}
                      onClose={() => setAnchorEl(null)}
                      TransitionComponent={Fade}
                    >
                      {currentUser?.roles[0]?.name !== PREFERRED_ADMIN_ROLE && (
                        <MenuItemDropdownItem
                          onClick={() => {
                            navigate(ROUTES.CONFIGURE_HOTELS);
                            setAnchorEl(null);
                          }}
                        >
                          Configure Hotels
                        </MenuItemDropdownItem>
                      )}
                      {currentUser?.roles[0]?.name !== PREFERRED_ADMIN_ROLE && (
                        <MenuItemDropdownItem
                          onClick={() => {
                            navigate(ROUTES.EVENT_CATEGORIES);
                            setAnchorEl(null);
                          }}
                        >
                          Configure Event Categories
                        </MenuItemDropdownItem>
                      )}
                      <MenuItemDropdownItem
                        onClick={() => {
                          navigate(ROUTES.USERS);
                          setAnchorEl(null);
                        }}
                      >
                        Configure Users
                      </MenuItemDropdownItem>
                    </Menu>
                  </>
                )}
            </Grid>
            <Grid item xs sx={{ minWidth: 0 }}>
              <NavbarHotelDropdown />
            </Grid>
            <AnnouncementBlock />
          </Grid>
          <Grid
            item
            xs={3}
            cc={3}
            sx={{
              flexBasis: { md: 260, lg: 300 },
              maxWidth: { md: 260, lg: 300 },
              flexShrink: 0,
            }}
            container
            justifyContent="flex-end"
            direction="row"
            alignItems="center"
            wrap="nowrap"
          >
            <Grid
              item
              mx={2}
              xs={3.5}
              sx={{ flexBasis: { md: 100 }, maxWidth: { md: 100 }, flexShrink: 0 }}
            >
              <NavbarCurrencyDropdown />
            </Grid>
            <Grid item xs={7} sx={{ flex: { md: '1 1 0' }, minWidth: 0 }}>
              <Typography noWrap sx={{ color: '#e5e5e5', fontSize: { md: 13, lg: 15 }, ml: 2 }}>
                {currentUser?.username}
              </Typography>
            </Grid>
            <Grid item xs={1.5} sx={{ flexBasis: { md: 46 }, maxWidth: { md: 46 }, flexShrink: 0 }}>
              <NavbarUserDropdown />
            </Grid>
          </Grid>
        </Grid>
      </Toolbar>
    </AppBar>
  );
};
//
export default withTheme(Navbar);
